import { getRoofOffsetPerimeters } from "building-generation/elements/multigableRoofPerimeter";
import {
  convertRoofFaceInfoToFaceListArray,
  EXCLUDE_RAFTER_FACE_TYPE,
  getDiscardBSPsByLevel,
  getRoofsTopFacesByLevel,
  ONLY_RAFTER_FACE_TYPE,
} from "building-generation/elements/roofs";
import { getPlanarGraphFacesForRoomEnclosureIsNotNone } from "building-generation/utils/planarGraphPerimeters";
import type { Building } from "building-types/building/buildingTypes";
import { WallLateralDatumAnchor } from "building-types/datums/datumsTypesEnum";
import type { RoofHandle } from "building-types/handles/docEntityHandleTypes";
import type { GeneratedRoofSystem } from "building-types/roofs/generatedRoofSystemTypes";
import type { RoofFace } from "building-types/roofs/roofFace";
import type { Skylight } from "building-types/settings/roofTypes";
import type { Table } from "common/data/table";
import { assertIsNotUndefined, ensureDefined } from "common/utils/assertOfType";
import degreeToRadian from "common/utils/degreeToRadian";
import { formatInchesFraction } from "doc/measurementUtils";
import type { LayoutSettings } from "doc/plans/layout";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import type { Face } from "geom/brep";
import { buildBSPNode, buildPolygonsFromFaces, isPointInsideBSP, removeCoplanarLinesBSP } from "geom/bsp";
import type { NodeBSP, PolygonBSP } from "geom/bspTypes";
import { DIST_EPSILON, DIST_EPSILON_SQUARED, WindingDir } from "geom/core";
import { extrusionToBRep } from "geom/extrude";
import { intersectRay2PolyLine2Array, ray2PolyLine2IntersectionMakeUnique } from "geom/intersect";
import { poleOfInaccessibilityPolygon } from "geom/label";
import { line2Eval, line2Len, line2Tangent } from "geom/line2";
import { mat4LookAt, mat4Ident } from "geom/mat4";
import { offsetPolyLine2 } from "geom/offset";
import {
  planeIsCoplanar,
  planeIsHorizontal,
  planeNewXY,
  planeNormalizedDownwardSlopeVector,
  planeProjectPolyLine3,
} from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { polygonUnion } from "geom/polygon";
import {
  polyLine2Area,
  polyLine2CopySimplify,
  polyLine2EdgeEval,
  polyLine2EdgeTangent,
  polyLine2WindingDir,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine3EdgeTangent, polyLine3EvalEdge, polyLine3OnPlane } from "geom/polyLine3";
import type { Vec2 } from "geom/vec2";
import { vec2Add, vec2AngleBetweenCW, vec2Copy, vec2Dist, vec2Dot, vec2Rotate } from "geom/vec2";
import type { Vec3 } from "geom/vec3";
import { vec3Copy } from "geom/vec3";
import { xyPlane } from "gfx/convert";
import { getPolygonsAabb } from "./perspective";
import { POLYGON_OFFSET_FOR_UNION } from "./perspectiveConstants";
import { renderPerspectiveEntityPolygons } from "./perspectiveEntities";
import { evalRoofAnnotationSetting, getRoofAnnotationSettings } from "./planSettingsDefault";
import type { SceneBSP } from "./SceneBSP";

const MIN_OVERHANG_LABEL_VALUE = 1;

export interface RoofPlan {
  readonly building: Building;
  readonly width: number;
  readonly height: number;
  readonly planRotationRads: number;
  readonly layoutSettings: LayoutSettings;
}

export interface RoofSlopeAnnoInfo {
  readonly roofSlopeVector: Vec3;
  readonly annoCenterPt: Vec2;
  readonly simplified: boolean;
}

export interface RoofSkylightAnnoInfo {
  readonly rotationRads: number;
  readonly annoCenterPt: Vec2;
  readonly text: string;
  readonly roofHandle: RoofHandle;
}

function copyRoofSlopeAnnoInfo(anno: RoofSlopeAnnoInfo): RoofSlopeAnnoInfo {
  return {
    roofSlopeVector: vec3Copy(anno.roofSlopeVector),
    annoCenterPt: vec2Copy(anno.annoCenterPt),
    simplified: anno.simplified,
  };
}

export interface RoofOverhangDimInfo {
  readonly roofOverhangPt: Vec2;
  readonly blockPerimContactPt: Vec2;
  readonly overhangDistance: number;
  readonly edgeLen: number;
}

function copyRoofOverhangDimInfo(anno: RoofOverhangDimInfo): RoofOverhangDimInfo {
  return {
    roofOverhangPt: vec2Copy(anno.roofOverhangPt),
    blockPerimContactPt: vec2Copy(anno.blockPerimContactPt),
    overhangDistance: anno.overhangDistance,
    edgeLen: anno.edgeLen,
  };
}

export interface RoofRidgeVentAnnoInfo {
  readonly annoCenterPt: Vec2;
  readonly rotationRads: number;
  readonly roofHandle: RoofHandle;
}

export function extractRoofPlanPerimsAnnoInfo(building: Building): {
  uncoveredPerims: { [levelId: string]: PolyLine2[] };
  roofLevelPerims: { [levelId: string]: PolyLine2[] };
} {
  const {
    roofSystems: roofs,
    planarGraphs,
    levels,
    settings: { roof: roofSettings, wall: wallSettings },
    rooms,
    roomLevelIndices,
    roomsByLevel,
    wallMasses,
  } = building;

  const { overhangDatum = WallLateralDatumAnchor.OUTSIDE_FACE_OF_FRAMING } = roofSettings;

  const uncoveredPerims: { [levelId: string]: PolyLine2[] } = {};
  const roofLevelPerims: { [levelId: string]: PolyLine2[] } = {};

  for (const roofId in roofs) {
    const roofSystem = roofs[roofId];

    for (const subRoofId in roofSystem.roofs) {
      const subRoof = roofSystem.roofs[subRoofId];
      const { levelId } = subRoof;
      if (!roofLevelPerims[levelId]) {
        roofLevelPerims[levelId] = [];
      }

      for (const face of subRoof.roofTopFaces) {
        const proj = projectFaceOuterLoopToPlane(face.face, xyPlane);
        roofLevelPerims[levelId].push(proj);
      }
    }
  }

  for (const levelId in levels) {
    const level = levels[levelId];
    if (level.floorIndex < 0) {
      continue;
    }

    const levelPlanarGraph = planarGraphs[levelId];
    const wallMassSystem = wallMasses[levelId];
    const uncoveredPerimeterFaces = getPlanarGraphFacesForRoomEnclosureIsNotNone(
      rooms,
      roomLevelIndices,
      roomsByLevel[levelId],
      levelPlanarGraph,
    );

    const exteriorWallPerimeters = getRoofOffsetPerimeters({
      planarGraph: levelPlanarGraph.planarGraph,
      wallMassSystem,
      faceIndices: uncoveredPerimeterFaces,
      wallSettings,
      overhangDatum,
    });

    if (!uncoveredPerims[levelId]) {
      uncoveredPerims[levelId] = [];
    }

    uncoveredPerims[levelId].push(...exteriorWallPerimeters);
  }

  return {
    uncoveredPerims,
    roofLevelPerims,
  };
}

export function extractRoofPlanRidgeVentAnnoInfo(building: Building): RoofRidgeVentAnnoInfo[] {
  const roofRidgeVentAnnoInfos: RoofRidgeVentAnnoInfo[] = [];

  const { roofSystems: roofs } = building;

  for (const roofId in roofs) {
    const roof = roofs[roofId];

    const { roofs: subRoofs } = roof;
    for (const subRoofId in subRoofs) {
      const roofHandle: RoofHandle = { type: "ROOF", id: subRoofId };
      const { ridgeRails } = subRoofs[subRoofId].rails;
      for (const { ridgeVentRails, addRidgeVent } of ridgeRails) {
        if (!addRidgeVent) {
          continue;
        }

        for (const ridgeVentRail of ridgeVentRails) {
          const [x, y] = polyLine3EvalEdge(ridgeVentRail, 0, 0.5, true);
          const [tx, ty] = polyLine3EdgeTangent(ridgeVentRail, 0);

          roofRidgeVentAnnoInfos.push({
            annoCenterPt: [x, y],
            rotationRads: vec2AngleBetweenCW(tx, ty, 1, 0),
            roofHandle,
          });
        }
      }
    }
  }

  return roofRidgeVentAnnoInfos;
}

export function extractRoofPlanOverhangDimInfo(building: Building, noSettings?: boolean): RoofOverhangDimInfo[] {
  const sampleTs: ReadonlyArray<number> = [0.5, 0.25, 0.75];
  const rafterExtensionSampleTs: ReadonlyArray<number> = [0.25, 0.75];

  const {
    roofSystems: roofs,
    planarGraphs,
    levels,
    settings: { roof: roofSettings, wall: wallSettings, planset },
    rooms,
    roomLevelIndices,
    roomsByLevel,
    wallMasses,
  } = building;

  const planSettings = getRoofAnnotationSettings(planset, noSettings);

  const { overhangDatum = WallLateralDatumAnchor.OUTSIDE_FACE_OF_FRAMING } = roofSettings;

  // roof view plane
  const plane = planeNewXY();
  const levelRoofFaceInfos = getRoofsTopFacesByLevel(roofs, levels);
  const levelRoofFaces = convertRoofFaceInfoToFaceListArray(levelRoofFaceInfos, EXCLUDE_RAFTER_FACE_TYPE);
  const levelRafterExtensionsFaces = convertRoofFaceInfoToFaceListArray(levelRoofFaceInfos, ONLY_RAFTER_FACE_TYPE);

  const roofOverhangAnnoInfos: RoofOverhangDimInfo[] = [];

  for (const levelId in levels) {
    const level = levels[levelId];
    if (level.floorIndex < 0) {
      continue;
    }

    let faces = levelRoofFaces[levelId] ?? [];
    let rafterExtensionsFaces = levelRafterExtensionsFaces[levelId] ?? [];

    function filterFaceForOverhangAnnotation(face: RoofFace): boolean | undefined {
      return evalRoofAnnotationSetting(planSettings, face.annotationSettings, "includeRoofOverhangDimensions");
    }

    faces = faces.filter(filterFaceForOverhangAnnotation);
    rafterExtensionsFaces = rafterExtensionsFaces.filter(filterFaceForOverhangAnnotation);

    const discardBSPs = getDiscardBSPsByLevel(roofs, levelId);
    const levelPlanarGraph = planarGraphs[levelId];
    const wallMassSystem = wallMasses[levelId];
    const uncoveredPerimeterFaces = getPlanarGraphFacesForRoomEnclosureIsNotNone(
      rooms,
      roomLevelIndices,
      roomsByLevel[levelId],
      levelPlanarGraph,
    );

    const exteriorWallPerimeters = getRoofOffsetPerimeters({
      planarGraph: levelPlanarGraph.planarGraph,
      wallMassSystem,
      faceIndices: uncoveredPerimeterFaces,
      wallSettings,
      overhangDatum,
    });

    const coplanarAdjacentFaceUnions = unionAdjacentCoplanarFaces(faces, plane);
    labelRoofOverhangs(
      exteriorWallPerimeters,
      coplanarAdjacentFaceUnions,
      sampleTs,
      discardBSPs.filter((x) => !!x),
      roofOverhangAnnoInfos,
    );

    const coplanarAdjacentRafterFaceUnions = unionAdjacentCoplanarFaces(rafterExtensionsFaces, plane);
    labelRoofOverhangs(
      exteriorWallPerimeters,
      coplanarAdjacentRafterFaceUnions,
      rafterExtensionSampleTs,
      [],
      roofOverhangAnnoInfos,
    );
  }

  return roofOverhangAnnoInfos;
}

export function extractRoofPlanUncoveredPerimsAnnoInfo(building: Building): { [levelId: string]: PolyLine2[] } {
  const uncoveredPerims: { [levelId: string]: PolyLine2[] } = {};

  const {
    planarGraphs,
    levels,
    settings: { roof: roofSettings, wall: wallSettings },
    rooms,
    roomLevelIndices,
    roomsByLevel,
    wallMasses,
  } = building;

  const { overhangDatum = WallLateralDatumAnchor.OUTSIDE_FACE_OF_FRAMING } = roofSettings;

  for (const levelId in levels) {
    const level = levels[levelId];
    if (level.floorIndex < 0) {
      continue;
    }

    const levelPlanarGraph = planarGraphs[levelId];
    const wallMassSystem = wallMasses[levelId];
    const uncoveredPerimeterFaces = getPlanarGraphFacesForRoomEnclosureIsNotNone(
      rooms,
      roomLevelIndices,
      roomsByLevel[levelId],
      levelPlanarGraph,
    );

    const exteriorWallPerimeters = getRoofOffsetPerimeters({
      planarGraph: levelPlanarGraph.planarGraph,
      wallMassSystem,
      faceIndices: uncoveredPerimeterFaces,
      wallSettings,
      overhangDatum,
    });

    if (!uncoveredPerims[levelId]) {
      uncoveredPerims[levelId] = [];
    }

    uncoveredPerims[levelId].push(...exteriorWallPerimeters);
  }

  return uncoveredPerims;
}

export function extractRoofPlanSkylightAnnoInfo(building: Building): RoofSkylightAnnoInfo[] {
  const roofSkylightAnnoInfos: RoofSkylightAnnoInfo[] = [];

  const { skylights, levels } = building;

  for (const levelId in levels) {
    const level = levels[levelId];
    if (level.floorIndex < 0) {
      continue;
    }

    labelRoofSkylights(skylights, roofSkylightAnnoInfos);
  }

  return roofSkylightAnnoInfos;
}

interface RoofFaceInfo {
  projectedOutline: PolyLine2;
  roofPlane: Plane | undefined;
  area: number;
  isDormer: boolean;
}

const MinimumFaceAreaForSlopeTag = 300;

export function getSlopeAnnos(
  { roofSystems: roofs, settings: { planset } }: Building,
  simplifiedSlopeAnno: boolean = false,
  noSettings?: boolean,
): RoofSlopeAnnoInfo[] {
  const slopeAnnoInfos: RoofSlopeAnnoInfo[] = [];
  const planes: Plane[] = [];
  const polygons: PolygonBSP[] = [];

  // helper functions
  const getPlaneIndex = ({ plane }: Face): number => {
    let currPlaneIndex: number = -1;
    for (let i = 0, l = planes.length; i < l; i++) {
      if (planeIsCoplanar(planes[i], plane, MULTIGABLE_BSP_EPSILON)) {
        currPlaneIndex = i;
        break;
      }
    }

    if (currPlaneIndex < 0) {
      currPlaneIndex = planes.length;
      planes.push(plane);
    }

    return currPlaneIndex;
  };

  const addPolygonsFromFace = (face: Face, isSimplified: boolean = false) => {
    const entityId = `${getPlaneIndex(face)}_${isSimplified ? 1 : 0}`;

    buildPolygonsFromFaces({
      faces: [face],
      polygons,
      extendedOptions: {
        entityId,
      },
    });
  };

  const addPolygonsFromFaces = (faces: Face[], isSimplified: boolean = false) => {
    for (const face of faces) {
      addPolygonsFromFace(face, isSimplified);
    }
  };

  const planSettings = getRoofAnnotationSettings(planset, noSettings);

  const collectRoofTops = (roofSystem: GeneratedRoofSystem) => {
    const { roofs: subRoofs } = roofSystem;

    for (const subRoofId in subRoofs) {
      const { roofTopPlates, trimsRoofMat, roofPlanSettings, isDormer } = subRoofs[subRoofId];

      const includeSlopeAnnotation = isDormer
        ? evalRoofAnnotationSetting(planSettings, roofPlanSettings, "includeDormerRoofSlopeAnnotation")
        : evalRoofAnnotationSetting(planSettings, roofPlanSettings, "includeRoofSlopeAnnotation");

      if (!includeSlopeAnnotation) {
        continue;
      }

      for (const roofTop of roofTopPlates) {
        const brepRoofTop = extrusionToBRep(roofTop);
        const [, topFace] = brepRoofTop.faces;

        addPolygonsFromFace(topFace);
      }

      for (const { faces } of trimsRoofMat) {
        addPolygonsFromFaces(faces, true);
      }
    }
  };

  // collect all the roof tops using a plane index as entityId
  for (const roofId in roofs) {
    collectRoofTops(roofs[roofId]);
  }

  // build the BSP with the collected polygons
  const root = buildBSPNode({ polygons });
  const aabb = getPolygonsAabb(polygons);
  removeCoplanarLinesBSP(root, polygons);
  const sceneBSP: SceneBSP = { root, aabb };

  // render it as entity polygons (for keeping the entity ids)
  const viewMatrix = mat4LookAt([0, 0, 1000], [0, 0, 0], [0, 1, 0]);
  const prjMatrix = mat4Ident();

  const entityPolylines = renderPerspectiveEntityPolygons({
    sceneBSP,
    viewMatrix,
    isPerspectiveMatrix: false,
    prjMatrix,
  });

  // group all the rendered polygons sharing the same entity id
  const groups: { [key: string]: PolyLine2[] } = {};
  for (const { entityId, polylines } of entityPolylines) {
    if (!groups[entityId]) {
      groups[entityId] = [];
    }

    groups[entityId].push(...polylines);
  }

  // try to join polygons from the same group (same entity id)
  const offsets: number[] = [POLYGON_OFFSET_FOR_UNION, 0.25];
  for (const groupId in groups) {
    const polylines = groups[groupId];

    if (!polylines?.length) {
      continue;
    }

    let unions: PolyLine2[][] | undefined;

    let joined = false;
    for (const offset of offsets) {
      try {
        unions = polygonUnion(polylines.map((x) => [offsetPolyLine2(x, -offset)]));
        joined = true;
        break;
      } catch (e) {
        console.error(e);
        console.error(polylines);
      }
    }

    if (!joined) {
      continue;
    }

    assertIsNotUndefined(unions);

    groups[groupId] = [];
    for (const union of unions) {
      groups[groupId].push(...union);
    }
  }

  // iterate groups
  for (const groupId in groups) {
    const polylines = groups[groupId];
    const [planeId, isSimplifiedId] = groupId.split("_");
    const planeIndex = parseInt(planeId);
    const isSimplified = parseInt(isSimplifiedId) !== 0;
    const plane = planes[planeIndex];

    // Avoids a crash on zero slopes. See issue BDG-192 in Linear for context.
    if (planeIsHorizontal(plane)) {
      continue;
    }

    // classify the polygons (outer vs inner)
    const holePolylines: PolyLine2[] = [];
    const outlinePolylines: PolyLine2[] = [];
    for (const polyline of polylines) {
      if (polyLine2WindingDir(polyline) === WindingDir.CCW) {
        holePolylines.push(polyline);
      } else {
        outlinePolylines.push(polyline);
      }
    }

    // add one slope annotation per outer polygon
    for (const polyline of outlinePolylines) {
      const area = polyLine2Area(polyline);
      if (area < MinimumFaceAreaForSlopeTag) {
        continue;
      }

      const [cX, cY] = ensureDefined(poleOfInaccessibilityPolygon([polyline, ...holePolylines]));
      const roofSlopeVector = planeNormalizedDownwardSlopeVector(plane);
      if (!roofSlopeVector) {
        continue;
      }
      slopeAnnoInfos.push(
        copyRoofSlopeAnnoInfo({
          roofSlopeVector,
          annoCenterPt: [cX, cY],
          simplified: simplifiedSlopeAnno || isSimplified,
        }),
      );
    }
  }

  return slopeAnnoInfos;
}

function unionAdjacentCoplanarFaces(roofFaces: RoofFace[], viewPlane: Plane): RoofFaceInfo[] {
  const projectedFaceInfos: RoofFaceInfo[][] = [];

  for (const { face, isDormer } of roofFaces) {
    const projFaceOutline = projectFaceOuterLoopToPlane(face, viewPlane);
    if (polyLine2Area(projFaceOutline) < DIST_EPSILON_SQUARED) {
      continue;
    }

    const projectedOutline = offsetPolyLine2(projFaceOutline, -POLYGON_OFFSET_FOR_UNION);
    const area = polyLine2Area(projectedOutline);
    let firstPlaneInArray: Plane | undefined;
    let indexToAddTo = -1;

    for (const planeIndex of Object.keys(projectedFaceInfos)) {
      const { roofPlane } = projectedFaceInfos[planeIndex][0];
      assertIsNotUndefined(roofPlane);
      firstPlaneInArray = roofPlane;
      if (!planeIsCoplanar(face.plane, roofPlane)) {
        continue;
      }
      indexToAddTo = parseInt(planeIndex);
    }

    if (indexToAddTo !== -1) {
      const list = projectedFaceInfos[indexToAddTo];
      list.push({ projectedOutline, roofPlane: firstPlaneInArray, area, isDormer });
      continue;
    }

    projectedFaceInfos.push([{ projectedOutline, roofPlane: face.plane, area, isDormer }]);
  }

  // union adjacent coplanar faces
  const unionedCoplanarFaces: RoofFaceInfo[] = [];

  for (const coplanarFaceInfos of projectedFaceInfos) {
    const { roofPlane, projectedOutline, area, isDormer: firstIsDormer } = coplanarFaceInfos[0];

    if (coplanarFaceInfos.length === 1) {
      const enlargedOutline = offsetPolyLine2(projectedOutline, POLYGON_OFFSET_FOR_UNION);
      unionedCoplanarFaces.push({
        projectedOutline: enlargedOutline,
        roofPlane: roofPlane,
        area,
        isDormer: firstIsDormer,
      });
      continue;
    }

    let isDormer = firstIsDormer;
    for (const coplanarFaceInfo of coplanarFaceInfos) {
      isDormer = isDormer && coplanarFaceInfo.isDormer;
    }

    const outlines = coplanarFaceInfos.map((x) => x.projectedOutline);
    let unions: PolyLine2[];

    try {
      unions = polygonUnion(outlines.map((x) => [x])).flat();
    } catch (e) {
      console.error(e);
      console.error(outlines);
      continue;
    }

    for (const union of unions) {
      const projectedOutline = polyLine2CopySimplify(union);
      const area = polyLine2Area(projectedOutline);

      if (area < DIST_EPSILON_SQUARED) {
        continue;
      }
      unionedCoplanarFaces.push({ projectedOutline, roofPlane, area, isDormer });
    }
  }

  return unionedCoplanarFaces;
}

function projectFaceOuterLoopToPlane(face: Face, plane: Plane): PolyLine2 {
  const p3 = polyLine3OnPlane(face.loops[0], face.plane);
  return planeProjectPolyLine3(plane, p3);
}

function labelRoofOverhangs(
  wallsPerimeterWRTOverhangDatum: PolyLine2[],
  coplanarAdjacentFaceUnions: RoofFaceInfo[],
  sampleTs: readonly number[],
  discardBSPs: NodeBSP[],
  roofOverhangAnnoInfos: RoofOverhangDimInfo[],
) {
  const longestEdgeUniqueOverhangMap = {};

  for (const perimeterPolyLine2 of wallsPerimeterWRTOverhangDatum) {
    for (const { projectedOutline: proj } of coplanarAdjacentFaceUnions) {
      for (let i = 0; i < proj.length - 2; i += 2) {
        for (const sampleT of sampleTs) {
          sampleRoofEdgeForVariousOverhangDepths(
            sampleT,
            proj[i + 0],
            proj[i + 1],
            proj[i + 2],
            proj[i + 3],
            [perimeterPolyLine2],
            longestEdgeUniqueOverhangMap,
            discardBSPs,
          );
        }
      }
    }
  }

  for (const overhangEntryId in longestEdgeUniqueOverhangMap) {
    const overhangEntry = longestEdgeUniqueOverhangMap[overhangEntryId];
    roofOverhangAnnoInfos.push(copyRoofOverhangDimInfo(overhangEntry));
  }
}

function labelRoofSkylights(skylights: Table<Skylight>, roofSkylightAnnoInfos: RoofSkylightAnnoInfo[]) {
  for (const skylightId in skylights) {
    const skylight = skylights[skylightId];
    const { width, height, opening, hostSubRoofId } = skylight;
    const roofHandle: RoofHandle = { type: "ROOF", id: hostSubRoofId };
    assertIsNotUndefined(opening);
    const { frontOutline3 } = opening;

    const projectedOutline = planeProjectPolyLine3(planeNewXY(), frontOutline3);
    const baseEdgeIdx = 3;

    const [cX, cY] = polyLine2EdgeEval(projectedOutline, baseEdgeIdx, 0.5, true);
    const [tX, tY] = polyLine2EdgeTangent(projectedOutline, baseEdgeIdx, true);

    const rotationRads = vec2AngleBetweenCW(1, 0, tX, tY);
    const annoCenterPt = [cX, cY];
    const text = `${width}${height} SKY`;

    roofSkylightAnnoInfos.push({
      rotationRads,
      annoCenterPt,
      text,
      roofHandle,
    });
  }
}

function sampleRoofEdgeForVariousOverhangDepths(
  sampleT: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
  blockPerim: PolyLine2[],
  longestUniqueOverhangMap: {
    [key: string]: RoofOverhangDimInfo;
  },
  discardBSPs: NodeBSP[],
) {
  const samplePt = line2Eval(ax, ay, bx, by, sampleT, true);
  const edgeLen = line2Len(ax, ay, bx, by);
  if (edgeLen < 18) {
    return;
  }

  const [samplePtX, samplePtY] = samplePt;
  const lineVec = line2Tangent(ax, ay, bx, by, true);
  const [rayX, rayY] = vec2Rotate(lineVec[0], lineVec[1], degreeToRadian(90));

  // discard the sample points that are fully bellow other sub-roofs
  const [discardPtX, discardPtY] = vec2Add(samplePtX, samplePtY, -rayX, -rayY);
  for (const discardBSP of discardBSPs) {
    if (isPointInsideBSP(discardBSP, discardPtX, discardPtY, 0, false, MULTIGABLE_BSP_EPSILON)) {
      return;
    }
  }

  const intersections = intersectRay2PolyLine2Array(blockPerim, samplePtX, samplePtY, rayX, rayY);
  const filteredIntersections = intersections.filter((int) => {
    const distance = vec2Dist(int.x, int.y, samplePtX, samplePtY);
    return int.t >= 0 && distance < 36 && distance >= MIN_OVERHANG_LABEL_VALUE;
  });

  ray2PolyLine2IntersectionMakeUnique(filteredIntersections);

  if (filteredIntersections.length === 0 || filteredIntersections[0] === undefined) {
    return;
  }

  const firstIntersection = filteredIntersections[0];
  const { x, y, loopIndex, edgeIndex } = firstIntersection;
  const overhangDistance = vec2Dist(samplePtX, samplePtY, x, y);
  // discard the negative ones
  if (overhangDistance <= DIST_EPSILON) {
    return;
  }

  const overhangDistanceForId = formatInchesFraction(overhangDistance, 8, false, true);
  const blockLoop = blockPerim[loopIndex];
  const adjIndex = edgeIndex * 2;

  const [edgeVecX, edgeVecY] = line2Tangent(
    blockLoop[adjIndex],
    blockLoop[adjIndex + 1],
    blockLoop[adjIndex + 2],
    blockLoop[adjIndex + 3],
  );

  //if ray and intersected angles are not at 90 degrees, then ignore intersection.
  const angle2 = vec2AngleBetweenCW(rayX, rayY, edgeVecX, edgeVecY) - Math.PI * 0.5;
  if (Math.abs(angle2) > DIST_EPSILON) {
    return;
  }

  const roofOverhangAnnoInfo: RoofOverhangDimInfo = {
    roofOverhangPt: [samplePtX, samplePtY],
    blockPerimContactPt: [firstIntersection.x, firstIntersection.y],
    overhangDistance: overhangDistance,
    edgeLen: edgeLen,
  };

  const angle = Math.round(vec2AngleBetweenCW(rayX, rayY, 1, 0));
  const dot = Math.round(vec2Dot(rayX, rayY, firstIntersection.x, firstIntersection.y));
  const hash = `${angle}_${dot}_${overhangDistanceForId}`;

  if (longestUniqueOverhangMap[hash]) {
    const prevOverhang = longestUniqueOverhangMap[hash];
    if (edgeLen <= prevOverhang.edgeLen) {
      return;
    }
  }

  longestUniqueOverhangMap[hash] = copyRoofOverhangDimInfo(roofOverhangAnnoInfo);
}
